import { LitElement, html, css, nothing, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import "./searchable-select";
import { AuthController } from '../user-area/controllers/auth-controller';
import { JOTFORM_IDS, showJotForm } from "../utils/showJotForm";

export interface SisuNotas {
  matematica: number;
  cienciasHumanas: number;
  cienciasNatureza: number;
  linguagens: number;
  redacao: number;
}

export interface SisuFiltros {
  estado: string;
  cidade: string;
  curso: string;
  modalidade: string;
}

export interface SisuResultado {
  curso: string;
  universidade: string;
  campus: string;
  cidade: string;
  estado: string;
  modalidade: string;
  notaCorte: number;
  suaNota: number;
  diferenca: number;
  aprovado: boolean;
  horario: string;
  pesos?: {
    exatas: number;
    humanas: number;
    natureza: number;
    linguagens: number;
    redacao: number;
  };
}

@customElement("simulador-sisu")
export class SimuladorSisu extends LitElement {
  authController = new AuthController(this);

  createRenderRoot() {
    return this;
  }

  @state() private etapaAtual: number = 1;
  @state() private notas: SisuNotas = {
    matematica: 0,
    cienciasHumanas: 0,
    cienciasNatureza: 0,
    linguagens: 0,
    redacao: 0,
  };
  @state() private filtros: SisuFiltros = {
    estado: "",
    cidade: "",
    curso: "",
    modalidade: "",
  };

  @state() private resultados: SisuResultado[] = [];
  @state() private carregando: boolean = false;
  @state() private estados: any[] = [];
  @state() private cursos: any[] = [];
  @state() private cidades: any[] = [];
  @state() private modalidades: string[] = [];
  @state() private erros: { [key: string]: string } = {};
  @state() private isMobile: boolean = false;
  @state() private sisuId: number | null = null;
  @state() private feedbackType: 'positive' | 'negative' | null = null;

  @state() private linkIsCopying: boolean = false;

  connectedCallback() {
    super.connectedCallback();
    this.carregarDados();
    // event resize
    window.addEventListener("resize", () => {
      this.getIsMobile();
    });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", () => {
      this.getIsMobile();
    });
  }

  protected firstUpdated(_changedProperties: PropertyValues): void {
    this.getIsMobile();
  }

  private async carregarDados() {
    try {
      // Carregar estados via API
      const responseEstados = await fetch("/api/sisu.php?action=estados");
      const dataEstados = await responseEstados.json();

      if (dataEstados.success) {
        this.estados = dataEstados.data;
      } else {
        console.error("Erro ao carregar estados:", dataEstados.error);
        // Fallback para arquivo JSON
        const responseEstadosFallback = await fetch(
          "/layout/frontoffice/json/estados.json"
        );
        this.estados = await responseEstadosFallback.json();
      }

      // Modalidades serão carregadas dinamicamente quando um curso for selecionado
      this.modalidades = [];
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    }
  }

  private async carregarCursosPorEstado(estado: string) {
    if (!estado) return;

    try {
      const response = await fetch(
        `/api/sisu.php?action=cursos&estado=${estado}`
      );
      const data = await response.json();

      if (data.success) {
        this.cursos = data.data.map((curso) => ({
          value: curso,
          label: curso,
        }));

        // Auto-select if only one course available
        if (this.cursos.length === 1) {
          this.filtros = { ...this.filtros, curso: this.cursos[0].value };
        }
      } else {
        console.error("Erro ao carregar cursos:", data.error);
        // Fallback para arquivo JSON
        const responseFallback = await fetch(
          `/layout/frontoffice/json/cursos-por-estado/${estado}.json`
        );
        const dados = await responseFallback.json();

        const cursosUnicos = new Set<string>();
        Object.keys(dados).forEach((curso) => {
          cursosUnicos.add(curso);
        });

        this.cursos = Array.from(cursosUnicos).map((curso) => ({
          value: curso,
          label: curso,
        }));

        // Auto-select if only one course available
        if (this.cursos.length === 1) {
          this.filtros = { ...this.filtros, curso: this.cursos[0].value };
        }
      }
    } catch (error) {
      console.error("Erro ao carregar cursos:", error);
    }
  }

  private async carregarCidadesPorEstado(estado: string) {
    if (!estado) return;

    try {
      const response = await fetch(
        `/api/sisu.php?action=cidades&estado=${estado}`
      );
      const data = await response.json();

      if (data.success) {
        this.cidades = data.data.map((cidade) => ({
          value: cidade,
          label: cidade,
        }));

        // Auto-select if only one city available
        if (this.cidades.length === 1) {
          this.filtros = { ...this.filtros, cidade: this.cidades[0].value };
        }
      } else {
        console.error("Erro ao carregar cidades:", data.error);
        // Fallback para arquivo JSON
        const responseFallback = await fetch(
          `/layout/frontoffice/json/cursos-por-estado/${estado}.json`
        );
        const dados = await responseFallback.json();

        const cidadesUnicas = new Set<string>();
        Object.values(dados).forEach((modalidades: any) => {
          Object.values(modalidades).forEach((cursos: any) => {
            cursos.forEach((curso: any) => {
              cidadesUnicas.add(curso.cidade);
            });
          });
        });

        this.cidades = Array.from(cidadesUnicas).map((cidade) => ({
          value: cidade,
          label: cidade,
        }));

        // Auto-select if only one city available
        if (this.cidades.length === 1) {
          this.filtros = { ...this.filtros, cidade: this.cidades[0].value };
        }
      }
    } catch (error) {
      console.error("Erro ao carregar cidades:", error);
    }
  }

  private onNotaChange(materia: keyof SisuNotas, valor: string) {
    const nota = parseFloat(valor) || 0;
    this.notas = { ...this.notas, [materia]: nota };
    // Limpar erro específico quando usuário começa a digitar
    if (this.erros[materia]) {
      const novosErros = { ...this.erros };
      delete novosErros[materia];
      this.erros = novosErros;
    }
  }

  private async carregarModalidadesPorCurso(estado: string, curso: string) {
    if (!estado || !curso) return;

    try {
      const response = await fetch(
        `/api/sisu.php?action=modalidades&estado=${estado}&curso=${curso}`
      );
      const data = await response.json();

      if (data.success) {
        this.modalidades = data.data;

        // Auto-select if only one modality available
        if (this.modalidades.length === 1) {
          this.filtros = { ...this.filtros, modalidade: this.modalidades[0] };
        }
      } else {
        console.error("Erro ao carregar modalidades:", data.error);
      }
    } catch (error) {
      console.error("Erro ao carregar modalidades:", error);
    }
  }

  private onFiltroChange(campo: keyof SisuFiltros, valor: string) {
    this.filtros = { ...this.filtros, [campo]: valor };

    // Limpar erro específico quando usuário faz uma seleção
    if (this.erros[campo]) {
      const novosErros = { ...this.erros };
      delete novosErros[campo];
      this.erros = novosErros;
    }

    if (campo === "estado") {
      this.carregarCursosPorEstado(valor);
      this.carregarCidadesPorEstado(valor);
      // Limpar seleções dependentes
      this.filtros = { ...this.filtros, cidade: "", curso: "", modalidade: "" };
    } else if (campo === "curso") {
      this.carregarModalidadesPorCurso(this.filtros.estado, valor);
      // Limpar modalidade quando curso muda
      this.filtros = { ...this.filtros, modalidade: "" };
    }
  }

  private validarEtapa1(): boolean {
    return (
      Object.values(this.notas).every((nota) => nota > 0) &&
      this.filtros.estado !== "" &&
      this.filtros.curso !== "" &&
      this.filtros.modalidade !== ""
    );
  }

  private validarEtapa1ComErros(): {
    valido: boolean;
    erros: { [key: string]: string };
  } {
    const erros: { [key: string]: string } = {};

    // Validar notas
    Object.entries(this.notas).forEach(([materia, nota]) => {
      if (nota <= 0) {
        erros[materia] = "Preencha esta nota";
      }
    });

    // Validar filtros obrigatórios
    if (this.filtros.estado === "") {
      erros.estado = "Selecione um estado";
    }
    if (this.filtros.curso === "") {
      erros.curso = "Selecione um curso";
    }
    if (this.filtros.modalidade === "") {
      erros.modalidade = "Selecione uma modalidade";
    }

    return {
      valido: Object.keys(erros).length === 0,
      erros,
    };
  }

  private async simular() {
    const validacao = this.validarEtapa1ComErros();

    if (!validacao.valido) {
      this.erros = validacao.erros;
      return;
    }

    // Limpar erros se validação passou
    this.erros = {};
    this.etapaAtual = 2;

    this.carregando = true;

    try {
      // Usar API para simulação
      const body: any = {
        action: "simular",
        estado: this.filtros.estado,
        curso: this.filtros.curso,
        modalidade: this.filtros.modalidade,
        notas: this.notas,
      };

      // Add cidade if selected (optional) - only send if not empty
      if (this.filtros.cidade && this.filtros.cidade.trim() !== '') {
        body.cidade = this.filtros.cidade.trim();
      }

      // Add user credentials if available
      if (this.authController.user?.uuid && this.authController.token) {
        body.uuid = this.authController.user.uuid;
        body.token = this.authController.token;
      }

      const response = await fetch("/api/sisu.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        this.resultados = data.data.resultados || [];
        this.sisuId = data.data.sisuId || null;
        this.etapaAtual = 2;
        this.toggleClassToPageHeader();
        
        // Debug: verificar se há resultados
        if (this.resultados.length === 0) {
          console.warn("Nenhum resultado retornado da simulação", data);
        }
      } else {
        console.error("Erro na simulação:", data.error || data);
        this.carregando = false;
        alert("Erro ao simular. Por favor, tente novamente.");
      }
    } catch (error) {
      console.error("Erro na simulação:", error);
      this.carregando = false;
      alert("Erro ao conectar com o servidor. Por favor, tente novamente.");

    } finally {
      this.carregando = false;
      this.scrollToTop();
    }
  }

  private toggleClassToPageHeader() {
    document.querySelector('#sisuPageHeader')?.classList.toggle('sisu-page-header--stage-2');
  }

  private reiniciar() {
    this.etapaAtual = 1;
    this.notas = {
      matematica: 0,
      cienciasHumanas: 0,
      cienciasNatureza: 0,
      linguagens: 0,
      redacao: 0,
    };
    this.filtros = {
      estado: "",
      cidade: "",
      curso: "",
      modalidade: "",
    };
    this.resultados = [];
    this.erros = {};
    this.sisuId = null;
    this.toggleClassToPageHeader();
    this.scrollToTop();
  }

  private scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  private async copiarLink() {
    const url = "https://todamateria.com.br/simulador-sisu/";
    this.linkIsCopying = true;
    try {
      await navigator.clipboard.writeText(url);
      // You could add a toast notification here
      console.log("Link copiado para a área de transferência!");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      console.log("Link copiado para a área de transferência!");
    }
    setTimeout(() => {
      this.linkIsCopying = false;
    }, 3000);
  }

  private compartilharWhatsApp() {
    const url = "https://todamateria.com.br/simulador-sisu/";
    const texto = "Confira nosso Simulador SISU gratuito! 🎓";
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
      texto + " " + url
    )}`;
    window.open(whatsappUrl, "_blank");
  }

  private async compartilharMobile() {
    const url = "https://todamateria.com.br/simulador-sisu/";
    const texto = "Confira nosso Simulador SISU gratuito! 🎓";

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Simulador SiSU 2026 Online e Gratuito - Toda Matéria",
          text: texto,
          url: url,
        });
      } catch (err) {
        // User cancelled or error occurred
        console.log("Share cancelled or failed");
      }
    } else {
      // Fallback to copy link
      this.copiarLink();
    }
  }

  private renderEtapa1() {
    return html`
      <div class="sisu-etapa">
        <div class="sisu-section">
          <h2 class="sisu-titulo">
            <span class="icon-095-pencil"></span
            ><span>Insira suas notas do ENEM</span>
          </h2>

          <div class="sisu-notas-grid">
            <div class="sisu-nota-item sisu-nota-item--mat">
              <label for="matematica">Matemática</label>
              <input
                type="number"
                id="matematica"
                min="0"
                max="1000"
                step="0.1"
                class="${classMap({
                  "sisu-input-error": this.erros.matematica,
                })}"
                .value="${this.notas.matematica === 0
                  ? ""
                  : this.notas.matematica.toString()}"
                @input="${(e: Event) =>
                  this.onNotaChange(
                    "matematica",
                    (e.target as HTMLInputElement).value
                  )}"
                placeholder="Ex: 750"
                inputmode="decimal"
                pattern="[0-9]*"
              />
              ${this.erros.matematica
                ? html`
                    <div class="sisu-field-error">${this.erros.matematica}</div>
                  `
                : nothing}
            </div>

            <div class="sisu-nota-item sisu-nota-item--ch">
              <label for="ciencias-humanas">Ciências Humanas</label>
              <input
                type="number"
                id="ciencias-humanas"
                min="0"
                max="1000"
                step="0.1"
                class="${classMap({
                  "sisu-input-error": this.erros.cienciasHumanas,
                })}"
                .value="${this.notas.cienciasHumanas === 0
                  ? ""
                  : this.notas.cienciasHumanas.toString()}"
                @input="${(e: Event) =>
                  this.onNotaChange(
                    "cienciasHumanas",
                    (e.target as HTMLInputElement).value
                  )}"
                placeholder="Ex: 680"
                inputmode="decimal"
                pattern="[0-9]*"
              />
              ${this.erros.cienciasHumanas
                ? html`
                    <div class="sisu-field-error">
                      ${this.erros.cienciasHumanas}
                    </div>
                  `
                : nothing}
            </div>

            <div class="sisu-nota-item sisu-nota-item--cn">
              <label for="ciencias-natureza">Ciências da Natureza</label>
              <input
                type="number"
                id="ciencias-natureza"
                min="0"
                max="1000"
                step="0.1"
                class="${classMap({
                  "sisu-input-error": this.erros.cienciasNatureza,
                })}"
                .value="${this.notas.cienciasNatureza === 0
                  ? ""
                  : this.notas.cienciasNatureza.toString()}"
                @input="${(e: Event) =>
                  this.onNotaChange(
                    "cienciasNatureza",
                    (e.target as HTMLInputElement).value
                  )}"
                placeholder="Ex: 720"
                inputmode="decimal"
                pattern="[0-9]*"
              />
              ${this.erros.cienciasNatureza
                ? html`
                    <div class="sisu-field-error">
                      ${this.erros.cienciasNatureza}
                    </div>
                  `
                : nothing}
            </div>

            <div class="sisu-nota-item sisu-nota-item--lc">
              <label for="linguagens">Linguagens e Códigos</label>
              <input
                type="number"
                id="linguagens"
                min="0"
                max="1000"
                step="0.1"
                class="${classMap({
                  "sisu-input-error": this.erros.linguagens,
                })}"
                .value="${this.notas.linguagens === 0
                  ? ""
                  : this.notas.linguagens.toString()}"
                @input="${(e: Event) =>
                  this.onNotaChange(
                    "linguagens",
                    (e.target as HTMLInputElement).value
                  )}"
                placeholder="Ex: 650"
                inputmode="decimal"
                pattern="[0-9]*"
              />
              ${this.erros.linguagens
                ? html`
                    <div class="sisu-field-error">${this.erros.linguagens}</div>
                  `
                : nothing}
            </div>

            <div class="sisu-nota-item sisu-nota-item--essay">
              <label for="redacao">Redação</label>
              <input
                type="number"
                id="redacao"
                min="0"
                max="1000"
                step="0.1"
                class="${classMap({ "sisu-input-error": this.erros.redacao })}"
                .value="${this.notas.redacao === 0
                  ? ""
                  : this.notas.redacao.toString()}"
                @input="${(e: Event) =>
                  this.onNotaChange(
                    "redacao",
                    (e.target as HTMLInputElement).value
                  )}"
                placeholder="Ex: 900"
                inputmode="decimal"
                pattern="[0-9]*"
              />
              ${this.erros.redacao
                ? html`
                    <div class="sisu-field-error">${this.erros.redacao}</div>
                  `
                : nothing}
            </div>
          </div>
        </div>

        <div class="sisu-section">
          <h2 class="sisu-titulo">
            <span class="icon-094-hat"></span
            ><span>Onde você quer estudar?</span>
          </h2>

          <div class="sisu-filtros-grid">
            <div class="sisu-filtro-item">
              <label class="sisu-label" for="estado">Estado</label>
              
              <searchable-select
                name="estado"
                placeholder="Selecione o estado"
                .options="${JSON.stringify(this.estados.map(estado => ({ value: estado.sigla, label: estado.nome })))}"
                .selected="${this.filtros.estado}"
                @change="${(e: CustomEvent) => {
                  const value = e.detail?.value;
                  this.onFiltroChange("estado", value !== undefined ? value : "");
                }}"
                class="${classMap({ "sisu-input-error": this.erros.estado })}"
              ></searchable-select>
              ${this.erros.estado
                ? html`
                    <div class="sisu-field-error">${this.erros.estado}</div>
                  `
                : nothing}
            </div>

            <div class="sisu-filtro-item">
              <label
                for="cidade"
                class=${classMap({
                  "sisu-label" : true,
                  "sisu-label-disabled": !this.filtros.estado,
                })}
                >Cidade <span class="sisu-optional">(opcional)</span></label
              >
              <searchable-select
                name="cidade"
                placeholder="Todas as cidades"
                .options="${JSON.stringify(this.cidades)}"
                .selected="${this.filtros.cidade}"
                @change="${(e: CustomEvent) => {
                  const value = e.detail?.value;
                  this.onFiltroChange("cidade", value !== undefined ? value : "");
                }}"
                ?disabled="${!this.filtros.estado}"
              ></searchable-select>
            </div>

            <div class="sisu-filtro-item">
              <label
                for="curso"
                class=${classMap({
                  "sisu-label" : true,
                  "sisu-label-disabled": !this.filtros.estado,
                })}
                >Curso</label
              >
              <searchable-select
                name="curso"
                placeholder="Selecione o curso"
                .options="${JSON.stringify(this.cursos)}"
                .selected="${this.filtros.curso}"
                @change="${(e: CustomEvent) => {
                  const value = e.detail?.value;
                  this.onFiltroChange("curso", value !== undefined ? value : "");
                }}"
                class="${classMap({ "sisu-input-error": this.erros.curso })}"
                ?disabled="${!this.filtros.estado}"
              ></searchable-select>
              ${this.erros.curso
                ? html`
                    <div class="sisu-field-error">${this.erros.curso}</div>
                  `
                : nothing}
            </div>

            <div class="sisu-filtro-item">
              <label
                for="modalidade"
                class=${classMap({
                  "sisu-label" : true,
                  "sisu-label-disabled": !this.filtros.curso,
                })}
                >Modalidade</label
              >
              <searchable-select
                name="modalidade"
                placeholder="${this.modalidades.length === 0
                  ? this.filtros.curso
                    ? "Carregando modalidades..."
                    : "Selecione um curso primeiro"
                  : "Selecione a modalidade"}"
                .options="${JSON.stringify(this.modalidades.map(modalidade => ({ value: modalidade, label: modalidade })))}"
                .selected="${this.filtros.modalidade}"
                @change="${(e: CustomEvent) => {
                  const value = e.detail?.value;
                  this.onFiltroChange("modalidade", value !== undefined ? value : "");
                }}"
                class="${classMap({
                  "sisu-input-error": this.erros.modalidade,
                })}"
                ?disabled="${!this.filtros.curso}"
              ></searchable-select>
              ${this.erros.modalidade
                ? html`
                    <div class="sisu-field-error">${this.erros.modalidade}</div>
                  `
                : nothing}
            </div>
          </div>
        </div>

        <div class="sisu-botoes">
          <button class="tdm-btn" @click="${this.simular}">
            ${this.carregando ? "Simulando..." : "Simular agora →"}
          </button>
        </div>
      </div>
    `;
  }

  private getIsMobile() {
    this.isMobile = window.innerWidth < 1024;
  }

  private renderEtapa2() {
    if (!this.resultados || this.resultados.length === 0) {
      return html`
        <div class="sisu-etapa">
          <div class="sisu-section-resultado">
            <p>Nenhum resultado encontrado. Por favor, tente novamente.</p>
            <button class="tdm-btn tdm-btn--white" @click="${this.reiniciar}">
              Simular de novo <span class="icon-042-restart-bold"></span>
            </button>
          </div>
        </div>
      `;
    }

    const nota = this.resultados[0].suaNota;
    const aprovados = this.resultados.filter((r) => r.aprovado).length;
    const total = this.resultados.length;

    return html`
      <div class="sisu-etapa">
        <div class="sisu-section-resultado">
          <div class="sisu-resultado-info">
            <div class="sisu-resultado-info-col-left">
              <h2 class="sisu-resultado-titulo">
                Você marcou <span class="sisu-resultado-nota">${nota.toFixed(0)} pontos</span>! Veja as universidades ao seu alcance:
              </h2>
            </div>
          </div>

          <div class="sisu-resultados-grid">
            ${this.resultados.map(
              (resultado) => html`
                <div
                  class="sisu-resultado-card ${classMap({
                    aprovado: resultado.aprovado,
                  })}"
                >
                  <div class="sisu-card-header">
                    <h3 class="sisu-card-titulo">
                      ${resultado.universidade} - ${resultado.curso}
                    </h3>
                    <div class="sisu-card-subtitulo">
                      <span class="sisu-info-valor">${resultado.campus}</span>
                    </div>
                  </div>

                  <div class="sisu-card-footer">
                    <div class="sisu-card-footer-item-wrapper">
                      <div class="sisu-card-footer-item">
                        <span class="sisu-nota-label">Nota de corte:</span>
                        <span class="sisu-nota-value nota-corte"
                          >${resultado.notaCorte.toFixed(1)}</span
                        >
                      </div>
                      <div class="sisu-card-footer-item">
                        <span class="sisu-nota-label">Horário:</span>
                        <span class="sisu-nota-value"
                          >${resultado.horario}</span
                        >
                      </div>
                    </div>

                    <div
                      class="sisu-card-status${classMap({
                        aprovado: resultado.aprovado,
                        reprovado: !resultado.aprovado,
                      })}"
                    >
                      ${resultado.aprovado
                        ? html`<span>↑</span> Chances Altas`
                        : html`<span>↓</span> Chances Baixas`}
                    </div>
                  </div>
                </div>
              `
            )}
          </div>

          <div class="sisu-botoes sisu-results-actionbar">
            <div class="sisu-feedback-wrapper">
              <span class="sisu-feedback-title">Você estudou com o Toda Matéria em 2025?</span>
              <button class="tdm-btn tdm-btn--white ${classMap({ "sisu-feedback-btn-active": this.feedbackType === 'positive' })}" @click="${() => {
                this.feedbackType = 'positive';
                showJotForm(JOTFORM_IDS.SIMULADOR_SISU, this.authController.user?.id?.toString() ?? '', this.authController.user?.email ?? '');
                this.saveFeedback('positive')
              }}">
                <span class="icon icon-041-thumbs"></span>
              </button>
              <button class="tdm-btn tdm-btn--white ${classMap({ "sisu-feedback-btn-active": this.feedbackType === 'negative' })}" @click="${() => {
                this.feedbackType = 'negative';
                this.saveFeedback('negative');
              }}">
                <span class="icon icon-down icon-041-thumbs"></span>
              </button>
            </div>
            <button class="tdm-btn tdm-btn--white" @click="${this.reiniciar}">
              Simular de novo <span class="icon-042-restart-bold"></span>
            </button>
          </div>

          ${this.renderTmplusBanner()} ${this.renderShare()}
        </div>
      </div>
    `;
  }
  private saveFeedback(type: 'positive' | 'negative') {
    if (!this.sisuId) {
      console.error('SISU ID not available');
      return;
    }
    
    fetch('/api/sisu.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'feedback',
        type: type,
        sisuId: this.sisuId,
      }),
    });
  }

  private renderShare() {
    if (this.isMobile) {
      return html`
        <div class="sisu-resultado-share-mobile">
          <div class="sisu-resultado-share-title">
            Mostre pros seus amigos nosso<br />
            Simulador SISU gratuito:
          </div>
          <div class="sisu-share-buttons-mobile">
            <button
              class="sisu-share-btn-mobile sisu-share-btn-mobile--share"
              @click="${this.compartilharMobile}"
            >
              <span class="sisu-share-btn-text">Compartilhar</span>
              <span class="icon-share"></span>
            </button>
            <button
              class="sisu-share-btn-mobile sisu-share-btn-mobile--whatsapp"
              @click="${this.compartilharWhatsApp}"
            >
              <span class="sisu-share-btn-text">WhatsApp</span>
              <span class="icon-whatsapp"></span>
            </button>
          </div>
        </div>
      `;
    }

    return html`
      <div class="sisu-resultado-share-desktop">
        <div class="sisu-resultado-share-title">
          Mostre pros seus amigos nosso Simulador SISU gratuito:
        </div>
        <div class="sisu-share-container">
          <input
            type="text"
            class="sisu-share-url"
            value="https://todamateria.com.br/simulador-sisu/"
            readonly
          />
          <button
            class="sisu-share-btn sisu-share-btn--copy ${classMap({ "sisu-share-btn--copy-copying": this.linkIsCopying })}"
            @click="${this.copiarLink}"
            ?disabled="${this.linkIsCopying}"
            title="Copiar link"
          >
            ${this.linkIsCopying ? html`<span>Copiado </span><span class="icon-check"></span>` : html`<span class="icon-link"></span>`}
          </button>
          <button
            class="sisu-share-btn sisu-share-btn--whatsapp"
            @click="${this.compartilharWhatsApp}"
            title="Compartilhar no WhatsApp"
          >
            <span class="icon-whatsapp"></span>
          </button>
        </div>
      </div>
    `;
  }

  private renderTmplusBanner() {
    const nota = this.resultados[0].suaNota;
    
    return html`
      <div class="tmplus-banner">
          ${nota < 500
            ? html`
            <div class="tmplus-banner--header">
                <h2>
                  Ainda dá tempo de virar o jogo!
                </h2>
            </div>

            <div class="tmplus-banner--content">
              <div class="tmplus-banner--features">
                <ul class="tmplus-banner--features-list">
                  <li class="tmplus-banner--feature-item"><span>Com o plano certo e foco no essencial:</span></li>
                  <li class="tmplus-banner--feature-item">
                    <span class="enem-icon-complete-green-check"></span>
                    <span
                      ><b>Plano de estudos</b> com os temas que mais caem no ENEM</span>
                  </li>
                  <li class="tmplus-banner--feature-item">
                    <span class="enem-icon-complete-green-check"></span>
                    <span
                      ><b>Tira-dúvidas com IA</b> que responde como um professor particular</span>
                  </li>
                  <li class="tmplus-banner--feature-item">
                    <span class="enem-icon-complete-green-check"></span>
                    <span
                      ><b>Corretor de redação</b> para treinar o texto que garante pontos preciosos</span>
                  </li>
                </ul>
              </div>

              <div class="tmplus-banner--offer">
                <div class="tmplus-banner--offer-text">
                  Assine por apenas R$ 29,90/mês
                </div>

                <a href="/checkout/"
                  class="tmplus-banner--cta-button"
                  data-google-interstitial="false"
                  >Melhorar minha nota agora
                  <span class="icon icon-088-arrow"></span></a>

                <div class="tmplus-banner--card">
                  Compra 100% segura com PIX ou Cartão
                </div>
              </div>
            </div>
              `
            : html`
            <div class="tmplus-banner--header">
                <h2>
                  🎯 Você está no caminho certo. Agora é hora de garantir a sua vaga!
                </h2>
            </div>

            <div class="tmplus-banner--content">
              <div class="tmplus-banner--features">
                <ul class="tmplus-banner--features-list">
                  <li class="tmplus-banner--feature-item">
                    <span class="enem-icon-complete-green-check"></span>
                    <span
                      ><b>Simulados</b> realistas para testar seu desempenho e tempo de prova</span>
                  </li>
                  <li class="tmplus-banner--feature-item">
                    <span class="enem-icon-complete-green-check"></span>
                    <span
                      ><b>Corretor de redação</b> que mostra onde melhorar em cada competência</span>
                  </li>
                  <li class="tmplus-banner--feature-item">
                    <span class="enem-icon-complete-green-check"></span>
                    <span
                      ><b>Exercícios com explicações</b> para revisar o que mais cai no ENEM</span>
                  </li>
                </ul>
              </div>

              <div class="tmplus-banner--offer">
                <div class="tmplus-banner--offer-text">
                  Assine por apenas R$ 29,90/mês
                </div>

                <a href="/checkout/"
                  class="tmplus-banner--cta-button"
                  data-google-interstitial="false"
                  >Praticar com o TM+ agora
                  <span class="icon icon-088-arrow"></span></a>

                <div class="tmplus-banner--card">
                  Compra 100% segura com PIX ou Cartão
                </div>
              </div>
            </div>
              `}
      </div>
    `;
  }

  render() {
    return html`
      <div
        class="sisu-container ${this.etapaAtual === 2
          ? "sisu-container--resultado"
          : ""}"
      >
        ${this.etapaAtual === 1 ? this.renderEtapa1() : nothing}
        ${this.etapaAtual === 2 ? this.renderEtapa2() : nothing}
      </div>
    `;
  }
}
